import * as React from "react";
import cx from "classnames";

import { DatasheetItem } from "@modules/datasheets/components/datasheet-item";
import { DatasheetItemLabel } from "@modules/datasheets/components/datasheet-item-label";
import { LookupTableFieldProps, useLookupTableFieldController } from "./lookup-table-field.controller";
import { DatasheetFieldComponentHeadingPosition } from "@common/dto/datasheet/field-component/heading-position";
import { Flex } from "@common/components/Flex";
import { Hint } from "@common/components/hint";
import Dropdown, { components } from "@common/containers/fields/Dropdown";

import { FieldContainer } from "../field-container";

import { ControlProps } from "react-select/lib/components/Control";
import { MenuListComponentProps } from "react-select/lib/components/Menu";
import { Button } from "@common/components/Button";
import { ClickAwayListener } from "@common/components/ClickAwayListener";

import "./lookup-table-field.styles.scss";

export const LookupTableField: React.FC<LookupTableFieldProps> = React.memo((props) => {
  const { datasheetElement } = props;

  const {
    headingPlacedHorizontally,
    headingReference,
    headingSize,
    headingPosition,
    normalizeDropdownOptions,
    menuOpened,
    columns,
    selectedItem,
    handleOpenMenu,
    handleCloseMenu,
    DROPDOWN_PLACEHOLDER,
  } = useLookupTableFieldController(props);

  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={datasheetElement.original.showfieldtype}
      withText={datasheetElement.original.headdisplay}
      text={datasheetElement.original.headtext}
      img={datasheetElement.original.field.imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
    />
  );

  const getMenuStyles = React.useCallback(
    (css: React.CSSProperties) => ({
      ...css,
      width: undefined,
    }),
    []
  );

  const getMenuPortalStyles = React.useCallback((css: React.CSSProperties) => ({ ...css, zIndex: 9999 }), []);

  const getMenuListComponent = (props: MenuListComponentProps<{}>) => {
    return (
      <components.MenuList {...props} className="DatasheetsResultLookupTableField__dropdown__menu">
        <div className="DatasheetsResultLookupTableField__dropdown__menu__table">
          <Flex className="DatasheetsResultLookupTableField__dropdown__menu__table__header">
            {columns.map((column) => (
              <Flex
                alignItems="center"
                className="DatasheetsResultLookupTableField__dropdown__menu__table__header__row"
                style={{
                  width: `${100 / columns.length}%`,
                }}
                key={column.accessorKey}
              >
                {column.header}
              </Flex>
            ))}
          </Flex>
          {normalizeDropdownOptions.map((option) => (
            <Flex
              key={option.value}
              width="100%"
              className={cx("DatasheetsResultLookupTableField__dropdown__menu__table__body__row", {
                "DatasheetsResultLookupTableField__dropdown__menu__table__body__row--selected": false
                  // datasheetElement.original.field.value == option.value,
              })}
              // onClick={() => handleLookupTableFieldValueChange(option.value)}
            >
              {columns.map((column) => (
                <Flex
                  key={column.accessorKey}
                  alignItems="center"
                  className="DatasheetsResultLookupTableField__dropdown__menu__table__body__row__column"
                  style={{
                    width: `${100 / columns.length}%`,
                  }}
                >
                  {/* {option[column.accessorKey]} */}
                </Flex>
              ))}
            </Flex>
          ))}
        </div>
      </components.MenuList>
    );
  };

  const getControlComponent = ({ cx, children, ...rest }: ControlProps<{}>) => (
    <components.Control cx={() => {}} {...rest}>
      <Button
        onClick={menuOpened ? handleCloseMenu : handleOpenMenu}
        style={{
          width: datasheetElement.width,
          height: datasheetElement.height,
        }}
      >
        {selectedItem?.label}
      </Button>
    </components.Control>
  );

  return (
    <DatasheetItem
      style={{
        top:
          datasheetElement.coordinates.y -
          (datasheetElement.original.headpos === DatasheetFieldComponentHeadingPosition.top ? headingSize.height : 0),
        left:
          datasheetElement.coordinates.x -
          (datasheetElement.original.headpos === DatasheetFieldComponentHeadingPosition.left ? headingSize.width : 0),
        display: headingPlacedHorizontally ? "flex" : "block",
        alignItems: headingPlacedHorizontally ? "flex-start" : undefined,
      }}
    >
      {[DatasheetFieldComponentHeadingPosition.top, DatasheetFieldComponentHeadingPosition.left].includes(
        headingPosition
      ) && labelComponent}
      <Flex alignItems="center">
        <FieldContainer
          width={datasheetElement.original.width}
          height={datasheetElement.original.height}
          showBorder={datasheetElement.original.showborder}
        >
          <ClickAwayListener onClickAway={handleCloseMenu}>
            <Dropdown
              fullWidth
              searchable={false}
              selectedValue={selectedItem?.value}
              options={normalizeDropdownOptions}
              menuIsOpen={menuOpened}
              placeholder={DROPDOWN_PLACEHOLDER}
              // menuListComponent={datasheetElement.original.field.options?.length ? getMenuListComponent : undefined}
              menuListComponent={getMenuListComponent}
              controlComponent={getControlComponent}
              styles={{
                width: "100%",
                menu: getMenuStyles,
                menuPortal: getMenuPortalStyles,
              }}
              onBlur={handleCloseMenu}
              onMenuClose={handleCloseMenu}
            />
          </ClickAwayListener>
        </FieldContainer>
        {datasheetElement.original.helptext && <Hint description={datasheetElement.original.helptext} />}
      </Flex>
      {[DatasheetFieldComponentHeadingPosition.bottom, DatasheetFieldComponentHeadingPosition.right].includes(
        headingPosition
      ) && labelComponent}
    </DatasheetItem>
  );
});
